import { Button, DrawerFooter, Loading } from "@/components";
import {
  FormDatePicker,
  FormDropzoneMultiple,
  FormInput,
  FormSelect,
  FormSelectMultiple,
} from "@/components/Form";
import { DROPZONE_ACCEPT } from "@/constants/constants";
import { useGetChat, useGetListChatMember, useUpdateChat } from "@/hooks";
import { ECHAT_THEME, ECHAT_THEME_LABEL, ECHAT_THEME_TYPE, UpdateChatParams } from "@/types";
import { formatDateCustom } from "@/utils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  RoomInformationFormData,
  roomInformationResolver,
} from "../../schemas/RoomInfomationSchema";

interface Props {
  onClose: () => void;
  roomId: number;
}

const FormEdit: React.FC<Props> = ({ onClose, roomId }) => {
  // search input for cc select
  const [searchInput, setSearchInput] = useState<string>("");
  const initialImageRef = useRef<string | null>(null);

  const { control, handleSubmit, reset } = useForm({
    resolver: roomInformationResolver,
    defaultValues: {
      start_date: formatDateCustom(new Date()),
      end_date: formatDateCustom(new Date()),
    },
  });

  // query to update chat room information
  const { updateChat, isUpdatingChat } = useUpdateChat();

  // parse form data to data for update chat API
  const parseFormDataToUpdateChatParams = useCallback(
    (data: RoomInformationFormData): UpdateChatParams => {
      const imageItem = data.image.at(-1);
      const newFile =
        imageItem?.file instanceof File ? imageItem.file : undefined;

      let image: File | null | undefined;
      if (newFile) {
        image = newFile;
      } else if (initialImageRef.current && data.image.length === 0) {
        image = null;
      } else {
        image = undefined;
      }

      return {
        title: data.title,
        start_date: formatDateCustom(data.start_date),
        end_date: formatDateCustom(data.end_date),
        theme_type: data.theme_type.value as ECHAT_THEME_TYPE,
        theme: data.theme.value as ECHAT_THEME,
        description: data.description,
        image,
        viewer_ids:
          data.viewer_ids?.map((viewer) => viewer.value as number) || [],
      };
    },
    []
  );

  const saveRoomInformation = (data: RoomInformationFormData) => {
    // TODO: Implement save functionality
    const params = parseFormDataToUpdateChatParams(data);
    updateChat({ id: roomId, data: params }).then(onClose);
  };

  // Load options for select
  // theme type options
  const themeTypeOptions = useMemo(() => {
    return Object.entries(ECHAT_THEME_TYPE).map(([_, value]) => ({
      label: ECHAT_THEME_LABEL[value],
      value: value,
    }));
  }, []);

  // theme options
  const themeOptions = useMemo(() => {
    return Object.entries(ECHAT_THEME).map(([_, value]) => ({
      label: value,
      value: value,
    }));
  }, []);

  // Load initial data and fill to form
  const { chat, isLoading } = useGetChat(roomId);
  const initializedRoomIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!chat) return;
    // Only init once per room — refetch would overwrite user edits
    if (initializedRoomIdRef.current === roomId) return;
    initializedRoomIdRef.current = roomId;

    initialImageRef.current = chat.image || null;

    // List cc users excluding owner and pic
    const ownerId = chat.created_by?.id;
    const picId = chat.pic?.id;

    // List cc users excluding owner and pic
    const ccUsers = chat.chatUsers.filter(
      (user) => user.user_id !== ownerId && user.user_id !== picId
    );

    reset({
      title: chat.title,
      start_date: chat.start_date
        ? formatDateCustom(chat.start_date)
        : formatDateCustom(new Date()),
      end_date: formatDateCustom(chat.end_date),
      theme_type: { label: ECHAT_THEME_LABEL[chat.theme_type as ECHAT_THEME_TYPE], value: chat.theme_type },
      theme: { label: chat.theme, value: chat.theme },
      viewer_ids: ccUsers.map((userRelation) => ({
        label: userRelation.user?.full_name,
        value: userRelation.user?.id,
        description: userRelation.user?.email,
      })),
      description: chat.description,
      image: chat.image ? [{ file: null, url: chat.image }] : [],
    });
  }, [chat, reset, roomId]);

  // Get list cc to select
  const {
    members,
    isLoading: isLoadingViewers,
    fetchNextPage: fetchNextPageViewers,
    hasNextPage: hasNextPageViewers,
  } = useGetListChatMember(roomId, { search: searchInput, s: searchInput });

  const optionMembers = useMemo(() => {
    return members.map((member) => ({
      label: member.user_name,
      value: member.user_id,
      description: member.user_email,
    }));
  }, [members]);

  if (isLoading) return <Loading />;

  return (
    <div className="flex flex-col h-full">
      <form
        className="flex-1 p-4 space-y-4 overflow-auto"
        onSubmit={handleSubmit(saveRoomInformation)}
      >
        <FormInput
          label="タイトル"
          control={control}
          name="title"
          required
          maxLength={150}
        />

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-y-4">
          <FormDatePicker
            control={control}
            name="start_date"
            label="相談日"
            required
            popperPlacement="bottom-start"
          />

          <FormDatePicker
            control={control}
            name="end_date"
            label="解答納期"
            required
          />

          <FormSelect
            control={control}
            name="theme_type"
            options={themeTypeOptions}
            label="相談者の属性"
            required
          />

          <FormSelect
            control={control}
            name="theme"
            options={themeOptions}
            label="テーマ"
            required
          />
        </div>

        <FormSelectMultiple
          control={control}
          name="viewer_ids"
          options={optionMembers}
          label="CC"
          isLoadingMoreItems={isLoadingViewers}
          onLoadMoreItems={fetchNextPageViewers}
          stopCheckScrollToBottom={!hasNextPageViewers}
          enableOptionDescription
          placeholder="CCを選択する"
          allowSearchInput
          onSearchInputChange={setSearchInput}
        />

        <FormInput
          label="相談内容"
          control={control}
          name="description"
          required
          multiline
          rows={6}
          maxLength={1000}
        />

        <FormDropzoneMultiple
          control={control}
          name="image"
          accept={{
            ...DROPZONE_ACCEPT.IMAGE.COMMON,
            ...DROPZONE_ACCEPT.PDF,
          }}
          maxSizeInBytes={25 * 1024 * 1024} // 25MB
          errorFileSize="最大アップロードサイズを超過しています。サイズの上限は25MBです。"
          errorInvalidType="画像は PNG, JPG, JPGE, PDF のうちいずれかの形式のファイルを指定してください。"
          content={
            <div className="text-center text-sm space-y-2">
              <div className="text-light_primary">
                ファイルを選択するか、ここにドラッグ＆ドロップしてください。
              </div>
              <div className="text-light_secondary text-xs">
                推奨ファイル形式: PNG, JPG, JPGE, PDF
              </div>
              <div className="text-light_secondary text-xs">
                動画/ワード/エクセルなどは、初回ルーム作成後に送付可能です。
              </div>
              <div className="underline text-xs text-cyan">アップロード</div>
            </div>
          }
          maxFiles={1}
          syncMode="replace"
        />
      </form>

      <DrawerFooter>
        <Button
          type="button"
          buttonType="text"
          color="default"
          className="!h-12"
          onClick={onClose}
        >
          キャンセル
        </Button>

        <Button
          type="button"
          variant="solid"
          color="primary"
          className="!h-12"
          onClick={handleSubmit(saveRoomInformation)}
          isLoading={isUpdatingChat}
        >
          保存
        </Button>
      </DrawerFooter>
    </div>
  );
};

export default FormEdit;
